import React, { useState } from 'react';
import { Instagram, Mail } from 'lucide-react';
import { kingfishProtocol, unexpectedAutopsy, singularities, archives, apparelStudy, seriesDescriptions, artistInfo, contactInfo } from './mock';
import './Portfolio.css';

const Portfolio = () => {
  const [selectedArtwork, setSelectedArtwork] = useState(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const SectionDivider = () => (
    <div className="section-divider">
      <img src="/chrome_divider.png" alt="" className="divider-logo" />
    </div>
  );

  return (
    <div className="portfolio">
      {/* Navigation */}
      <nav className="nav">
        <div className="nav-container">
          <div className="nav-brand">
            <img src="/murke_logo.png" alt="Murke Dirty Graphics" className="brand-logo" />
          </div>
          <div className="nav-links">
            <button onClick={() => scrollToSection('kingfish')}>KINGFISH PROTOCOL</button>
            <button onClick={() => scrollToSection('autopsy')}>UNEXPECTED AUTOPSY</button>
            <button onClick={() => scrollToSection('singularities')}>SINGULARITIES</button>
            <button onClick={() => scrollToSection('archives')}>THE ARCHIVES</button>
            <button onClick={() => scrollToSection('apparel')}>APPAREL STUDY</button>
          </div>
        </div>
      </nav>

      {/* Kingfish Protocol Series */}
      <section id="kingfish" className="series-section">
        <div className="section-container">
          <h2 className="section-title">Kingfish Protocol</h2>
          <p className="series-description">{seriesDescriptions.kingfishProtocol}</p>
          
          <div className="gallery-grid">
            {kingfishProtocol.map((artwork) => (
              <div 
                key={artwork.id} 
                className="gallery-item"
                onClick={() => setSelectedArtwork(artwork)}
              >
                <div className="artwork-image-wrapper">
                  <img src={artwork.image} alt={artwork.title} className="artwork-image" />
                  <div className="artwork-overlay">
                    <div className="artwork-info">
                      <h3>{artwork.title}</h3>
                      <p>{artwork.year}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <SectionDivider />
      </section>

      {/* Unexpected Autopsy Series */}
      <section id="autopsy" className="series-section autopsy-section">
        <div className="section-container">
          <h2 className="section-title">Unexpected Autopsy</h2>
          <p className="series-description">{seriesDescriptions.unexpectedAutopsy}</p>
          
          <div className="gallery-grid">
            {unexpectedAutopsy.map((artwork) => (
              <div 
                key={artwork.id} 
                className="gallery-item"
                onClick={() => setSelectedArtwork(artwork)}
              >
                <div className="artwork-image-wrapper">
                  <img src={artwork.image} alt={artwork.title} className="artwork-image" />
                  <div className="artwork-overlay">
                    <div className="artwork-info">
                      <h3>{artwork.title}</h3>
                      <p>{artwork.year}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <SectionDivider />
      </section>

      {/* Singularities - Standalone Works */}
      <section id="singularities" className="series-section">
        <div className="section-container">
          <h2 className="section-title">Singularities</h2>
          <p className="series-description">{seriesDescriptions.singularities}</p>
          
          <div className="gallery-grid">
            {singularities.map((artwork) => (
              <div 
                key={artwork.id} 
                className="gallery-item"
                onClick={() => setSelectedArtwork(artwork)}
              >
                <div className="artwork-image-wrapper">
                  <img src={artwork.image} alt={artwork.title} className="artwork-image" />
                  <div className="artwork-overlay">
                    <div className="artwork-info">
                      <h3>{artwork.title}</h3>
                      <p>{artwork.year}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <SectionDivider />
      </section>

      {/* The Archives - Prototypes, Brand Concepts, Process Work */}
      <section id="archives" className="series-section archives-section">
        <div className="section-container">
          <h2 className="section-title">The Archives</h2>
          <p className="series-description">{seriesDescriptions.archives}</p>
          
          {archives.length > 0 ? (
            <div className="gallery-grid">
              {archives.map((artwork) => (
                <div 
                  key={artwork.id} 
                  className="gallery-item"
                  onClick={() => setSelectedArtwork(artwork)}
                >
                  <div className="artwork-image-wrapper">
                    <img src={artwork.image} alt={artwork.title} className="artwork-image" />
                    <div className="artwork-overlay">
                      <div className="artwork-info">
                        <h3>{artwork.title}</h3>
                        <p>{artwork.year}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-section">Coming Soon</p>
          )}
        </div>
        <SectionDivider />
      </section>

      {/* Apparel Study */}
      <section id="apparel" className="series-section">
        <div className="section-container">
          <h2 className="section-title">Apparel Study</h2>
          <p className="series-description">{seriesDescriptions.apparelStudy}</p>
          
          <div className="apparel-container">
            {apparelStudy.map((item) => (
              <div key={item.id} className="apparel-entry">
                <div className="apparel-gallery">
                  {item.images.map((image, index) => (
                    <div key={index} className="apparel-image-wrapper">
                      <img src={image} alt={`${item.title} - ${index + 1}`} className="apparel-image" />
                    </div>
                  ))}
                </div>
                <div className="apparel-info">
                  <h3 className="apparel-title">{item.title}</h3>
                  <p className="apparel-status">{item.status}</p>
                  <p className="apparel-description">{item.description}</p>
                  <button 
                    className="inquire-btn"
                    onClick={() => scrollToSection('contact')}
                  >
                    Inquire Within
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <SectionDivider />
      </section>

      {/* Artist Statement */}
      <section id="artist" className="artist-section">
        <div className="section-container">
          <h2 className="artist-title">The Mind of Michael Eduarde Thurston</h2>
          <p className="artist-statement">{artistInfo.statement}</p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="section-container">
          <h2 className="section-title">Contact</h2>
          <div className="contact-info-display">
            <p className="contact-description">For inquiries about available works, commissions, or collaborations:</p>
            <div className="contact-details">
              <a href={`mailto:${contactInfo.email}`} className="contact-link">{contactInfo.email}</a>
              <a href="https://www.instagram.com/murkedirtygraphics" target="_blank" rel="noopener noreferrer" className="contact-link">
                {contactInfo.instagram}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <img src="/chrome_logo.png" alt="Brand Mark" className="footer-logo" />
          </div>
          <div className="footer-links">
            <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
            <span className="separator">•</span>
            <a href="https://www.instagram.com/murkedirtygraphics" target="_blank" rel="noopener noreferrer">
              {contactInfo.instagram}
            </a>
          </div>
        </div>
      </footer>

      {/* Artwork Detail Modal */}
      {selectedArtwork && (
        <div className="modal-overlay" onClick={() => setSelectedArtwork(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedArtwork(null)}>×</button>
            <div className="modal-grid">
              <div className="modal-image">
                <img src={selectedArtwork.image} alt={selectedArtwork.title} />
              </div>
              <div className="modal-details">
                <h2>{selectedArtwork.title}</h2>
                <div className="modal-meta">
                  <p><strong>Series:</strong> {selectedArtwork.series}</p>
                  <p><strong>Year:</strong> {selectedArtwork.year}</p>
                  <p><strong>Dimensions:</strong> {selectedArtwork.dimensions}</p>
                </div>
                {selectedArtwork.description && (
                  <p className="modal-description">{selectedArtwork.description}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;
